import enGB from 'date-fns/locale/en-GB';
import { Range, DateRange } from 'react-date-range';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeDateRange,
  getDateRangeAsObjects,
  DateRangeState,
  DateRangeValue
} from 'features/date-range-selection/dateRangeSlice';
import { toUtc } from 'common/utils/date-helpers';
import { useMemo, useRef, useState } from 'react';
import { useClickAway, useWindowSize } from 'react-use';
import theme from 'tailwind.theme';
import { DateTime } from 'luxon';

const DateRangePicker = () => {
  const ref = useRef<HTMLDivElement>(null);

  const { width } = useWindowSize();

  const isMobile = useMemo(() => {
    return width < 768;
  }, [width]);

  const dispatch = useDispatch();
  const [openDateRange, setOpenDateRange] = useState(false);

  const { startDate, endDate, key } = useSelector(getDateRangeAsObjects);

  useClickAway(ref, () => {
    setOpenDateRange(false);
  });

  const handleClick = () => {
    setOpenDateRange((prevState: boolean) => !prevState);
  };

  const formatDates = (item: Range): DateRangeValue => {
    return {
      startDate: toUtc(item.startDate ?? new Date()),
      endDate: toUtc(item.endDate ?? new Date()),
      key
    };
  };

  const mapDateRangeToSerializable = (item: DateRangeValue): DateRangeState => {
    return {
      startDate: item.startDate.toISOString(),
      endDate: item.endDate.toISOString(),
      key: item.key
    };
  };

  const handleSelect = (item: Range) => {
    const payload = mapDateRangeToSerializable(formatDates(item));
    dispatch(changeDateRange(payload));
  };

  return (
    <>
      <div className="relative" ref={ref}>
        <div
          className="flex justify-between items-center border px-3 py-2 border-neutral-200 rounded bg-white"
          onClick={handleClick}
        >
          <i className="ra-icons-generic-calendar text-primary-700 pr-1 mr-1 flex"></i>
          <button className="text-sm text-center">
            {`${DateTime.fromJSDate(startDate).toFormat(
              'dd LLL'
            )} - ${DateTime.fromJSDate(endDate).toFormat('dd LLL')}`}
          </button>
          <i className="ra-icons-generic-down-arrow pl-3 pr-1 pt-1 pb-1 text-center text-xs flex"></i>
        </div>

        {openDateRange ? (
          <div
            className="absolute -translate-x-1/2 top-12 md:translate-x-0 md:right-0 
             z-40 min-w-max w-28 bg-light drop-shadow-xl rounded min-h-fit"
            id="date-dropdown-menu"
            aria-haspopup="true"
            role="menu"
          >
            <div className="rounded border-0">
              {/* the current day styling is controlled in our main as a css class override - thanks date range library! */}
              <DateRange
                rangeColors={[
                  theme.colors.navy,
                  theme.colors.navy,
                  theme.colors.gray
                ]}
                showMonthAndYearPickers={true}
                months={2}
                maxDate={new Date()}
                editableDateInputs={true}
                showPreview={true}
                direction={`${isMobile ? 'vertical' : 'horizontal'}`}
                ranges={[{ startDate, endDate, key }]}
                onChange={(item) => handleSelect(item.selection)}
                showMonthArrow={true}
                weekdayDisplayFormat="EEEEE"
                preventSnapRefocus={true}
                moveRangeOnFirstSelection={false}
                locale={enGB}
              />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default DateRangePicker;
